import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { UserRole } from '@repo/shared/types';
import { Badge } from '@repo/ui/badge';
import { Button } from '@repo/ui/button';
import { Card, CardContent } from '@repo/ui/card';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@repo/ui/form';
import { RadioGroup, RadioGroupItem } from '@repo/ui/radio-group';
import { useStep } from '@repo/ui/stepper';
import { CheckCircle2Icon, ShieldIcon } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { RouterInputs } from '@/providers/trpc-client';

type TCreateOfficerRoleForm = Pick<RouterInputs['organization']['inviteOfficer'], 'role'>;

const OFFICER_ROLE_PERMISSIONS: Record<
  TCreateOfficerRoleForm['role'],
  {
    title: string;
    desc: string;
  }[]
> = {
  [UserRole.ORG_MANAGER]: [
    {
      get title() {
        return t('auth.viewOrganizationClients');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAllClientsInTheOrganization');
      },
    },
    {
      get title() {
        return t('auth.viewOrganizationAllCreatedReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAllReportsCreatedByOther');
      },
    },
    {
      get title() {
        return t('auth.approveRejectClients');
      },
      get desc() {
        return t('extra.allowsTheOfficerToApproveOrRejectClientsBasedReport');
      },
    },
  ],
  [UserRole.ORG_OFFICER]: [
    {
      get title() {
        return t('auth.createKycAndKybReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToCreateBothKycAndKybReports');
      },
    },
    {
      get title() {
        return t('auth.viewAndManageOwnReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAndManageOnlyReportsThey');
      },
    },
    {
      get title() {
        return t('auth.viewAndManageClientsAddByThemselves');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAndManageOnlyClientsThey');
      },
    },
    {
      get title() {
        return t('auth.approveRejectClients');
      },
      get desc() {
        return t('extra.allowsTheOfficerToApproveOrRejectClientsBasedCreated');
      },
    },
  ],
  [UserRole.ORG_KYC_OFFICER]: [
    {
      get title() {
        return t('auth.createKycReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToCreateBothKycReportsToReview');
      },
    },
    {
      get title() {
        return t('auth.viewAndManageOwnReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAndManageOnlyReportsThey');
      },
    },
    {
      get title() {
        return t('auth.viewAndManageClientsAddByThemselves');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAndManageOnlyClientsThey');
      },
    },
    {
      get title() {
        return t('auth.approveRejectClients');
      },
      get desc() {
        return t('extra.allowsTheOfficerToApproveOrRejectClientsBasedCreated');
      },
    },
  ],
  [UserRole.ORG_KYB_OFFICER]: [
    {
      get title() {
        return t('auth.createKybReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToCreateKybReportsToReviewClients');
      },
    },
    {
      get title() {
        return t('auth.viewAndManageOwnReports');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAndManageOnlyReportsThey');
      },
    },
    {
      get title() {
        return t('auth.viewAndManageClientsAddByThemselves');
      },
      get desc() {
        return t('extra.allowsTheOfficerToViewAndManageOnlyClientsThey');
      },
    },
    {
      get title() {
        return t('auth.approveRejectClients');
      },
      get desc() {
        return t('extra.allowsTheOfficerToApproveOrRejectClientsBasedCreated');
      },
    },
  ],
};

export const OFFICER_ROLE_LABELS: Record<TCreateOfficerRoleForm['role'], string> = {
  get [UserRole.ORG_MANAGER]() {
    return t('extra.manager');
  },
  [UserRole.ORG_OFFICER]: 'KYC/KYB Officer',
  get [UserRole.ORG_KYC_OFFICER]() {
    return t('workspace.kycOfficer');
  },
  get [UserRole.ORG_KYB_OFFICER]() {
    return t('workspace.kybOfficer');
  },
};

type Props = {
  form: UseFormReturn<TCreateOfficerRoleForm>;
  onNext: () => void;
};

export const CreateOfficerRoleForm = ({ form, onNext }: Props) => {
  useLocale();

  const { previousStep } = useStep();
  const selectedRole = form.watch('role');

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(() => onNext())}>
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  {[
                    UserRole.ORG_MANAGER,
                    UserRole.ORG_OFFICER,
                    UserRole.ORG_KYC_OFFICER,
                    UserRole.ORG_KYB_OFFICER,
                  ].map((role) => (
                    <div
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                        field.value === role
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 hover:bg-muted/40'
                      }`}
                      key={role}
                    >
                      <RadioGroupItem className="mt-0.5" value={role} />
                      <div>
                        <p className="font-medium text-sm">{OFFICER_ROLE_LABELS[role]}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {selectedRole && OFFICER_ROLE_PERMISSIONS[selectedRole] && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-medium text-sm">
              <ShieldIcon className="h-4 w-4 text-primary" />
              {t('auth.permissionsFor')}{' '}
              <Badge variant="secondary">{OFFICER_ROLE_LABELS[selectedRole]}</Badge>
            </div>
            <div className="space-y-2">
              {OFFICER_ROLE_PERMISSIONS[selectedRole].map((perm) => (
                <Card key={perm.title}>
                  <CardContent className="flex items-start gap-3 px-4 py-3">
                    <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                    <div>
                      <p className="font-medium text-sm">{perm.title}</p>
                      <p className="text-muted-foreground text-xs">{perm.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button onClick={() => previousStep()} type="button" variant="outline">
            {t('auth.back')}{' '}
          </Button>
          <Button type="submit">{t('auth.continue')}</Button>
        </div>
      </form>
    </Form>
  );
};
